import React from 'react';
import { Checkbox } from 'antd';
import type { RouteDeviationVariant } from '@/apis/types';
import styles from './RouteDeviation.module.css';
import {
  ALTERNATIVE_ROUTE,
  MOST_USED_ROUTE,
  describeVariant,
} from './routeDeviationCopy';

interface RouteDeviationVariantListProps {
  variants: RouteDeviationVariant[];
  /** Trip IDs the user counts as normal. A group is ticked when it holds any of them. */
  tickedTripIds: ReadonlySet<string>;
  onToggle: (variant: RouteDeviationVariant) => void;
}

export const isTicked = (
  variant: RouteDeviationVariant,
  tickedTripIds: ReadonlySet<string>
): boolean => variant.trip_ids.some((id) => tickedTripIds.has(id));

/** The variant with the most deliveries; the first of equals, which the API lists by label. */
export const mostUsedLabel = (
  variants: RouteDeviationVariant[]
): string | undefined =>
  variants.reduce<RouteDeviationVariant | undefined>(
    (best, variant) =>
      !best || variant.trip_count > best.trip_count ? variant : best,
    undefined
  )?.label;

const RouteDeviationVariantList: React.FC<RouteDeviationVariantListProps> = ({
  variants,
  tickedTripIds,
  onToggle,
}) => {
  const total = variants.reduce((sum, variant) => sum + variant.trip_count, 0);
  const mostUsed = mostUsedLabel(variants);

  return (
    <div className={styles.variantList}>
      {variants.map((variant) => (
        <div
          key={variant.label}
          className={styles.variantRow}
          data-testid='route-deviation-variant'
        >
          <Checkbox
            className={styles.rowCheckbox}
            checked={isTicked(variant, tickedTripIds)}
            onChange={() => onToggle(variant)}
          >
            <span className={styles.variantLetter}>{variant.label}</span>
            <span className={styles.variantText}>
              <span className={styles.variantTitle}>
                {variant.label === mostUsed
                  ? MOST_USED_ROUTE
                  : ALTERNATIVE_ROUTE}
              </span>
              <span className={styles.variantMeta}>
                {describeVariant(variant, total)}
              </span>
            </span>
          </Checkbox>
        </div>
      ))}
    </div>
  );
};

export default RouteDeviationVariantList;
